import React from "react";
import ChapterShell from "@/tutorial/ChapterShell";
import {
  Section,
  AnnotatedScreen,
  Step,
  TipBox,
  InfoBox,
} from "@/tutorial/components/TutorialComponents";

function ChartSection({ id, eyebrow, title, src, points, tip, children }) {
  return (
    <Section id={id} eyebrow={eyebrow} title={title}>
      <AnnotatedScreen src={src} alt={title} annotations={[]} />
      <div className="text-sm font-semibold text-foreground">
        Comment le lire :
      </div>
      <div className="space-y-3">
        {points.map((p, i) => (
          <Step key={i} number={i + 1} title={p.t}>
            {p.d}
          </Step>
        ))}
      </div>
      {children}
      {tip && <TipBox title="Astuce de lecture avancée">{tip}</TipBox>}
    </Section>
  );
}

export default function Chapter5() {
  return (
    <ChapterShell id={5}>
      <Section
        id="section-5-0"
        eyebrow="5.0"
        title="Six graphiques pour lire un budget en vies"
      >
        <p>
          Le tableau de bord vitaliste articule six visualisations. Chacune
          répond à une question précise sur le rapport entre l'argent public
          et la vie humaine. Cette section les explore une à une.
        </p>
      </Section>

      <ChartSection
        id="section-5-1"
        eyebrow="5.1"
        title="Donut « Anatomie des prélèvements »"
        src="/tutorial/images/chapter5-donut.png"
        points={[
          { t: "Quatre segments fondateurs", d: "Impôts directs · cotisations sociales · TVA · charge de la dette." },
          { t: "Lecture en pourcentage", d: "Chaque arc indique la part de votre charge fiscale globale." },
          { t: "Survol interactif", d: "Survolez un segment pour afficher la valeur en jours de vie." },
          { t: "Comparaison rapide", d: "Identifiez le poste qui pèse le plus sur votre temps." },
          { t: "Code couleur stable", d: "Bleu institutionnel pour les impôts directs, or pour la dette." },
        ]}
        tip="Si la dette occupe plus de 25 % du donut, le pays vit déjà sur le temps de ses enfants : voir la dette intergénérationnelle au chapitre 9."
      />

      <ChartSection
        id="section-5-2"
        eyebrow="5.2"
        title="Calendrier 365 jours — Jour de libération fiscale"
        src="/tutorial/images/chapter5-calendar.png"
        points={[
          { t: "365 cellules colorées", d: "Une case = un jour de l'année calendaire." },
          { t: "Cases rouges = État", d: "Les premiers jours de vie de l'année vont aux prélèvements obligatoires." },
          { t: "Cases vertes = vous", d: "À partir du jour de libération, vous travaillez pour vous." },
          { t: "Date repère", d: "La date affichée en grand est le jour de libération fiscale du profil." },
          { t: "Comparaison entre profils", d: "L'ouvrier SMIG est libéré plus tôt que le cadre supérieur." },
        ]}
      >
        <InfoBox title="Concept de jour de libération fiscale">
          Le « jour de libération fiscale » répond à la question :{" "}
          <em>« à partir de quand commencez-vous à travailler pour vous-même ? »</em>{" "}
          C'est l'expression la plus visuelle de la formule vitaliste.
        </InfoBox>
      </ChartSection>

      <ChartSection
        id="section-5-3"
        eyebrow="5.3"
        title="Treemap « Budget national en vies »"
        src="/tutorial/images/chapter5-treemap.png"
        points={[
          { t: "Surface ∝ jours de vie", d: "La taille de chaque rectangle est proportionnelle à son coût vitaliste." },
          { t: "Hiérarchie sectorielle", d: "Santé, éducation, sécurité, justice, infrastructures…" },
          { t: "Sous-rectangles", d: "Chaque secteur peut se décomposer en sous-postes (zoom au clic)." },
          { t: "Lecture immédiate", d: "Identifie en un coup d'œil le poste qui dévore le plus de vies." },
          { t: "Couleurs sectorielles cohérentes", d: "Mêmes couleurs que le radar (§5.4) pour la lecture croisée." },
        ]}
        tip="Comparez deux années (chapitre 9) en superposant deux treemaps pour visualiser les arbitrages politiques structurels."
      />

      <ChartSection
        id="section-5-4"
        eyebrow="5.4"
        title="Radar « Retour vitaliste sectoriel »"
        src="/tutorial/images/chapter5-radar.png"
        points={[
          { t: "5 axes sectoriels", d: "Santé, éducation, sécurité, justice, infrastructures." },
          { t: "Aire bleue = ce que vous payez", d: "En jours de vie investis dans chaque secteur." },
          { t: "Aire dorée = ce que vous recevez", d: "Service rendu estimé dans la même unité." },
          { t: "Asymétrie révélatrice", d: "Si l'aire bleue dépasse largement l'aire dorée : déficit vitaliste." },
          { t: "Sélecteur de profil", d: "L'arbitrage diffère selon les profils — un cadre cotise plus mais peut bénéficier moins." },
        ]}
      />

      <ChartSection
        id="section-5-5"
        eyebrow="5.5"
        title="Bar chart comparatif multi-profils"
        src="/tutorial/images/chapter5-bar.png"
        points={[
          { t: "Comparez jusqu'à 3 profils", d: "Ouvrier · fonctionnaire · cadre, par exemple." },
          { t: "Barres par catégorie", d: "Jours sacrifiés, mois, % vie fiscale alignés en colonnes." },
          { t: "Échelle commune", d: "Permet la lecture proportionnelle entre profils." },
          { t: "Bandes colorées", d: "Vert / orange / rouge pour situer chaque profil dans les seuils du chapitre 4." },
        ]}
        tip="Activez l'export PNG (chapitre 8) sur ce graphique pour diffuser une infographie d'inégalité fiscale parlante."
      />

      <ChartSection
        id="section-5-6"
        eyebrow="5.6"
        title="Line chart — évolution de la dette"
        src="/tutorial/images/chapter5-line.png"
        points={[
          { t: "Axe horizontal : années", d: "Projection sur 10, 20 et 30 ans des chocs budgétaires." },
          { t: "Axe gauche : dette en monnaie", d: "Trajectoire nominale en milliards." },
          { t: "Axe droit : dette en années-vie", d: "Conversion vitaliste en années de vie hypothéquées." },
          { t: "Zone d'alerte", d: "Au-delà d'un seuil rouge, la trajectoire devient irréversible à génération constante." },
          { t: "Curseur d'années", d: "Glissez pour explorer chaque année et lire l'arbitrage cumulé." },
        ]}
      >
        <InfoBox title="Lecture intergénérationnelle">
          Cette courbe est le pivot de l'analyse intergénérationnelle vue au
          chapitre 9. Elle convertit la dette publique en années de vie
          préemptées sur les enfants à naître.
        </InfoBox>
      </ChartSection>
    </ChapterShell>
  );
}
